import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { api, type User, type ChangePasswordRequest } from "../api/client";
import type { ThemeKey } from "@sparklab/types";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (userName: string, password: string) => Promise<void>;
  register: (userName: string, password: string, inviteCode: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserPoints: (points: number) => void;
  updateTheme: (theme: ThemeKey) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  refreshCurrentUser: () => Promise<User | null>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshCurrentUser = useCallback(async (): Promise<User | null> => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setUser(null);
      return null;
    }

    try {
      const userData = await api.getCurrentUser();
      // Ensure points is a number, default to 0 if missing
      const safeUserData = {
        ...userData,
        points: typeof userData.points === 'number' ? userData.points : 0
      };
      setUser(safeUserData);
      return safeUserData;
    } catch (error: any) {
      console.error("Failed to refresh current user", error);
      // Only logout if unauthorized (401), otherwise keep the session (might be network error)
      if (error?.status === 401) {
        localStorage.removeItem("userId");
        setUser(null);
      }
      return null;
    }
  }, []);

  // 初始化时检查是否已登录
  useEffect(() => {
    refreshCurrentUser().finally(() => setLoading(false));
  }, [refreshCurrentUser]);

  const login = async (userName: string, password: string) => {
    const userData = await api.login({ user_name: userName, password });
    setUser(userData);
    localStorage.setItem("userId", userData.id.toString());
  };

  const register = async (userName: string, password: string, inviteCode: string) => {
    const userData = await api.register({ user_name: userName, password, invite_code: inviteCode });
    setUser(userData);
    localStorage.setItem("userId", userData.id.toString());
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      localStorage.removeItem("userId");
    }
  };

  const updateUserPoints = (points: number) => {
    setUser((prev) => (prev ? { ...prev, points: points ?? 0 } : prev));
  };

  const updateTheme = async (theme: ThemeKey) => {
    const updatedUser = await api.updateTheme(theme);
    setUser(updatedUser);
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    await api.changePassword(data);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUserPoints,
        updateTheme,
        changePassword,
        refreshCurrentUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

